import {
  DutchAuctionOrder,
  OfferOrder,
  OrderType,
  ReserveAuctionOrder,
  SignedSaleOrder,
} from "@/api/orders/types";
import { ART_ORDER_EIP712_DOMAIN, EXCHANGE_EIP712_DOMAIN } from "./types";
import { ZeroAddress } from "ethers";
import { ArtOrderFull, ArtOrderOffer } from "@/api/art-order/types";
import { getArtOrderDomainData, getExchangeDomainData } from "./helpers";
import { getDaysInSeconds } from "@/shared/dates";
import { getFeeCut } from "@/shared/currencies";

function getEIP712SignatureParams(rawSignature: string) {
  const sig = rawSignature.substring(2);

  const r = "0x" + sig.substring(0, 64);
  const s = "0x" + sig.substring(64, 128);
  const v = parseInt(sig.substring(128, 130), 16);

  return { v, r, s };
}

const getSignedSaleMessage = (sale: SignedSaleOrder) => {
  return JSON.stringify({
    types: {
      EIP712Domain: EXCHANGE_EIP712_DOMAIN,
      Sale: [
        { name: "orderNonce", type: "uint256" },
        { name: "nftContract", type: "address" },
        { name: "tokenId", type: "uint256" },
        { name: "paymentErc20TokenAddress", type: "address" },
        { name: "price", type: "uint256" },
        { name: "makerCut", type: "uint256" },
        { name: "takerCut", type: "uint256" },
        { name: "royaltiesCut", type: "uint256" },
        { name: "royaltiesRecipient", type: "address" },
        { name: "collectiveCut", type: "uint256" },
        { name: "collectiveRecipient", type: "address" },
        { name: "buyer", type: "address" },
        { name: "expiresAt", type: "uint256" },
      ],
    },
    primaryType: "Sale",
    domain: getExchangeDomainData(sale.blockchain!),
    message: {
      orderNonce: sale.orderNonce,
      nftContract: sale.nftContract,
      tokenId: sale.tokenId,
      paymentErc20TokenAddress: sale.paymentErc20TokenAddress,
      price: sale.price!.toString(),
      makerCut: getFeeCut(sale.price, sale.makerFee),
      takerCut: getFeeCut(sale.price, sale.takerFee),
      royaltiesCut: getFeeCut(sale.price, sale.royaltiesFee),
      royaltiesRecipient: sale.royaltiesRecipient,
      collectiveCut: getFeeCut(sale.price, sale.collectiveFee),
      collectiveRecipient: sale.collectiveRecipient,
      buyer: sale.type === OrderType.PRIVATE_AUCTION ? sale.buyer : ZeroAddress,
      expiresAt: sale.expiresAt,
    },
  });
};

const getSignedOfferMessage = (offer: OfferOrder) => {
  return JSON.stringify({
    types: {
      EIP712Domain: EXCHANGE_EIP712_DOMAIN,
      Offer: [
        { name: "orderNonce", type: "uint256" },
        { name: "nftContract", type: "address" },
        { name: "tokenId", type: "uint256" },
        { name: "paymentErc20TokenAddress", type: "address" },
        { name: "price", type: "uint256" },
        { name: "makerCut", type: "uint256" },
        { name: "takerCut", type: "uint256" },
        { name: "royaltiesCut", type: "uint256" },
        { name: "royaltiesRecipient", type: "address" },
        { name: "collectiveCut", type: "uint256" },
        { name: "collectiveRecipient", type: "address" },
        { name: "expiresAt", type: "uint256" },
        { name: "isForCollection", type: "bool" },
      ],
    },
    primaryType: "Offer",
    domain: getExchangeDomainData(offer.blockchain!),
    message: {
      orderNonce: offer.orderNonce,
      nftContract: offer.nftContract,
      tokenId: offer.tokenId,
      paymentErc20TokenAddress: offer.paymentErc20TokenAddress,
      price: offer.totalPrice?.toString(),
      makerCut: getFeeCut(offer.price, offer.makerFee),
      takerCut: getFeeCut(offer.price, offer.takerFee),
      royaltiesCut: getFeeCut(offer.price, offer.royaltiesFee),
      royaltiesRecipient: offer.royaltiesRecipient,
      collectiveCut: getFeeCut(offer.price, offer.collectiveFee),
      collectiveRecipient: offer.collectiveRecipient,
      expiresAt: offer.expiresAt,
      isForCollection: offer.isForCollection,
    },
  });
};

const getDutchAuctionMessage = (dutchAuction: DutchAuctionOrder) => {
  return JSON.stringify({
    types: {
      EIP712Domain: EXCHANGE_EIP712_DOMAIN,
      DutchAuction: [
        { name: "orderNonce", type: "uint256" },
        { name: "nftContract", type: "address" },
        { name: "tokenId", type: "uint256" },
        { name: "paymentErc20TokenAddress", type: "address" },
        { name: "startingPrice", type: "uint256" },
        { name: "endingPrice", type: "uint256" },
        { name: "makerFeePercentage", type: "uint256" },
        { name: "takerFeePercentage", type: "uint256" },
        { name: "royaltiesPercentage", type: "uint256" },
        { name: "royaltiesRecipient", type: "address" },
        { name: "collectiveFeePercentage", type: "uint256" },
        { name: "collectiveRecipient", type: "address" },
        { name: "startingAt", type: "uint256" },
        { name: "duration", type: "uint256" },
      ],
    },
    primaryType: "DutchAuction",
    domain: getExchangeDomainData(dutchAuction.blockchain!),
    message: {
      orderNonce: dutchAuction.orderNonce,
      nftContract: dutchAuction.nftContract,
      tokenId: dutchAuction.tokenId,
      paymentErc20TokenAddress: dutchAuction.paymentErc20TokenAddress,
      startingPrice: dutchAuction.startingPrice.toString(),
      endingPrice: dutchAuction.endingPrice.toString(),
      makerFeePercentage: dutchAuction.makerFee,
      takerFeePercentage: dutchAuction.takerFee,
      royaltiesPercentage: dutchAuction.royaltiesFee,
      royaltiesRecipient: dutchAuction.royaltiesRecipient,
      collectiveFeePercentage: dutchAuction.collectiveFee,
      collectiveRecipient: dutchAuction.collectiveRecipient,
      startingAt: dutchAuction.startingAt,
      duration: dutchAuction.duration,
    },
  });
};

const getReserveAuctionMessage = (reserveAuction: ReserveAuctionOrder) => {
  return JSON.stringify({
    types: {
      EIP712Domain: EXCHANGE_EIP712_DOMAIN,
      ReserveAuction: [
        { name: "orderNonce", type: "uint256" },
        { name: "price", type: "uint256" },
        { name: "nftContract", type: "address" },
        { name: "tokenId", type: "uint256" },
        { name: "paymentErc20TokenAddress", type: "address" },
        { name: "makerFeePercentage", type: "uint256" },
        { name: "takerFeePercentage", type: "uint256" },
        { name: "royaltiesPercentage", type: "uint256" },
        { name: "royaltiesRecipient", type: "address" },
        { name: "collectiveFeePercentage", type: "uint256" },
        { name: "collectiveRecipient", type: "address" },
      ],
    },
    primaryType: "ReserveAuction",
    domain: getExchangeDomainData(reserveAuction.blockchain!),
    message: {
      orderNonce: reserveAuction.orderNonce,
      price: reserveAuction.isBid
        ? reserveAuction.totalPrice.toString()
        : reserveAuction.price.toString(),
      nftContract: reserveAuction.nftContract,
      tokenId: reserveAuction.tokenId,
      paymentErc20TokenAddress: reserveAuction.paymentErc20TokenAddress,
      makerFeePercentage: reserveAuction.makerFee,
      takerFeePercentage: reserveAuction.takerFee,
      royaltiesPercentage: reserveAuction.royaltiesFee,
      royaltiesRecipient: reserveAuction.royaltiesRecipient,
      collectiveFeePercentage: reserveAuction.collectiveFee,
      collectiveRecipient: reserveAuction.collectiveRecipient,
    },
  });
};

const getReserveAuctionBidMessage = (reserveAuction: ReserveAuctionOrder) => {
  return JSON.stringify({
    types: {
      EIP712Domain: EXCHANGE_EIP712_DOMAIN,
      ReserveAuctionBid: [
        { name: "orderNonce", type: "uint256" },
        { name: "price", type: "uint256" },
        { name: "nftContract", type: "address" },
        { name: "tokenId", type: "uint256" },
        { name: "paymentErc20TokenAddress", type: "address" },
        { name: "makerFeePercentage", type: "uint256" },
        { name: "takerFeePercentage", type: "uint256" },
        { name: "royaltiesPercentage", type: "uint256" },
        { name: "royaltiesRecipient", type: "address" },
        { name: "collectiveFeePercentage", type: "uint256" },
        { name: "collectiveRecipient", type: "address" },
      ],
    },
    primaryType: "ReserveAuctionBid",
    domain: getExchangeDomainData(reserveAuction.blockchain!),
    message: {
      orderNonce: reserveAuction.orderNonce,
      price: reserveAuction.isBid
        ? reserveAuction.totalPrice.toString()
        : reserveAuction.price.toString(),
      nftContract: reserveAuction.nftContract,
      tokenId: reserveAuction.tokenId,
      paymentErc20TokenAddress: reserveAuction.paymentErc20TokenAddress,
      makerFeePercentage: reserveAuction.makerFee,
      takerFeePercentage: reserveAuction.takerFee,
      royaltiesPercentage: reserveAuction.royaltiesFee,
      royaltiesRecipient: reserveAuction.royaltiesRecipient,
      collectiveFeePercentage: reserveAuction.collectiveFee,
      collectiveRecipient: reserveAuction.collectiveRecipient,
    },
  });
};

const getSignedArtOrderOfferMessage = (
  artOrder: ArtOrderFull,
  offer: ArtOrderOffer,
  isOfferAccepted?: boolean,
) => {
  //CreateOrder(uint256 nonce,address orderer,address artist,uint256 price,uint256 timeframe,address paymentErc20TokenAddress)
  return JSON.stringify({
    types: {
      EIP712Domain: ART_ORDER_EIP712_DOMAIN,
      CreateOrder: [
        { name: "nonce", type: "uint256" },
        { name: "orderer", type: "address" },
        { name: "artist", type: "address" },
        { name: "price", type: "uint256" },
        { name: "timeframe", type: "uint256" },
        { name: "paymentErc20TokenAddress", type: "address" },
      ],
    },
    primaryType: "CreateOrder",
    domain: getArtOrderDomainData(artOrder.blockchain!),
    message: {
      nonce: artOrder.nonce,
      orderer: artOrder.author.address,
      artist: isOfferAccepted
        ? artOrder.acceptedOffer?.author.address
        : offer.author?.address,
      price: offer.price.toString(),
      timeframe: getDaysInSeconds(offer.timeframe),
      paymentErc20TokenAddress: artOrder.budgetPaymentERC20TokenAddress,
    },
  });
};

const getSignedArtOrderExtendMessage = (
  artOrder: ArtOrderFull,
  offer: ArtOrderOffer,
) => {
  //ExtendOrder(uint256 nonce,address orderer,address artist,uint256 price,uint256 timeframe,address paymentErc20TokenAddress,uint256 newDeadline)
  return JSON.stringify({
    types: {
      EIP712Domain: ART_ORDER_EIP712_DOMAIN,
      ExtendOrder: [
        { name: "nonce", type: "uint256" },
        { name: "orderer", type: "address" },
        { name: "artist", type: "address" },
        { name: "price", type: "uint256" },
        { name: "timeframe", type: "uint256" },
        { name: "paymentErc20TokenAddress", type: "address" },
        { name: "newDeadline", type: "uint256" },
      ],
    },
    primaryType: "ExtendOrder",
    domain: getArtOrderDomainData(artOrder.blockchain!),
    message: {
      nonce: artOrder.nonce,
      orderer: artOrder.author.address,
      artist: artOrder.acceptedOffer?.author.address,
      price: offer.price.toString(),
      timeframe: getDaysInSeconds(offer.timeframe),
      paymentErc20TokenAddress: artOrder.budgetPaymentERC20TokenAddress,
      newDeadline: artOrder.deadline,
    },
  });
};

export {
  getEIP712SignatureParams,
  getSignedOfferMessage,
  getSignedSaleMessage,
  getDutchAuctionMessage,
  getReserveAuctionMessage,
  getReserveAuctionBidMessage,
  getSignedArtOrderOfferMessage,
  getSignedArtOrderExtendMessage,
};
